import React from 'react'
import _ from 'lodash'

import {Alert, Button, Icon, Input as AntdInput, Select as AntdSelect} from 'antd'

import {Spinkit} from 'ui'

/**
 * This is a naive and simple form toolkit
 *
 * If you find yourself in more then this is probably not the right option
 * for you.
 *
 * Features:
 *  - validation
 *  - feedback (based on validation)
 *  - value transformation
 *
 * It expects that the `onSubmit` prop is set and returns a promise.
 *
 * Example:
 *
 * <Form input={dataObject} onSubmit={updateDataObject}>
 *    <Form.Updated>Submit returned successfully!</Form.Updated>
 *    <Form.Error>Some error occurred during submit</Form.Error>
 *
 *    <Input param="name" label="name" validate={customValidateFun} />
 *
 *    <Input
 *       param="countryCode"
 *       label="name"
 *       validate={{size: 2}}
 *       transform={[(str) => str.toUpperCase()]} />
 *       />
 *
 *    <Submit>Update data object</Submit>
 *    <Reset>Reset Form</Reset>
 * </Form>
 */

const doValidation = function(param, value, props, input, patch) {
   const validate = props.validate
   let res

   if (!_.has(patch, param)) {
      return true
   }

   // function validation may return an object with meta data (like password score)
   // check that an error field is present to determine if it's an error
   if (_.isFunction(validate)) {
      res = validate(value, props, input, patch)

      return _.isObject(res) && res.error ? res : true
   } else if (_.isObject(validate)) {
      if (validate.size && validate.size < (value || '').length) {
         return {
            error: `${param} exceeds maximum size of ${validate.size} characters`
         }
      } else {
         return true
      }
   } else {
      return true
   }
}

export class Form extends React.Component {
   constructor(p) {
      super(p)

      this.state = {
         patch:         p.defaultValues || {},
         // state should be one of "default", "updating", "error", or "updated"
         // representing noSubmit, or the state of the submit
         state:         'default',
         updatePromise: null
      }

      this._mounted = false
      this._inputs = {}

      this.registerInput = this.registerInput.bind(this)
      this.onChange = this.onChange.bind(this)
      this.resetForm = this.resetForm.bind(this)
      this.submitForm = this.submitForm.bind(this)
      // this.submitState = this.submitState.bind(this)
   }

   static get propTypes() {
      return {
         onSubmit:  React.PropTypes.func.isRequired,
         input:     React.PropTypes.object.isRequired,
         children:  React.PropTypes.node.isRequired,
         transform: React.PropTypes.func
      }
   }

   static get defaultProps() {
      return {
         input: {}
      }
   }

   componentDidMount() {
      this._mounted = true
   }

   componentWillUnmount() {
      this._mounted = false
   }

   static get childContextTypes() {
      return {
         form:        React.PropTypes.object,
         onChange:    React.PropTypes.func,
         resetForm:   React.PropTypes.func,
         submitForm:  React.PropTypes.func,
         submitState: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object]),
         patch:       React.PropTypes.object,
         input:       React.PropTypes.object
      }
   }

   getChildContext() {
      return {
         form:        this,
         onChange:    this.onChange,
         resetForm:   this.resetForm,
         submitForm:  this.submitForm,
         submitState: this.submitState,
         patch:       this.patch(),
         input:       this.input()
      }
   }

   patch() {
      return this.state.patch
   }

   input() {
      return this.props.input
   }

   registerInput(props) {
      this._inputs[props.param] = props
   }

   onChange(ev, meta, val) {
      if (!meta || (!ev && undefined === val)) {
         console.warn('can\'t set form data, either missing meta or ev/val')

         return false
      }

      if (!meta.param) {
         console.warn('can\'t set form data, meta.param not set')

         return false
      }

      let value = undefined === val ? ev.target.value : val

      if ('checkbox' === meta.type) {
         value = !(_.isString(value) ? 'true' === value : value)
      }

      if (meta.transform) {
         value = _.reduce(meta.transform, (acc, f) => f(acc), value)
      }

      this.setState(state => _.set(state, `patch.${meta.param}`, value))

      return true
   }

   resetForm(ev) {
      ev.preventDefault()

      this.setState({patch: {}})
   }

   validateForm() {
      const {patch} = this.state,
         {input} = this.props

      return _.reduce(
         this._inputs,
         function(acc, props, _k) {
            let {param} = props,
               value = _.get(patch, param),
               result = doValidation(param, value, props, input, patch)

            if (true === result) {
               return acc
            } else {
               acc[param] = doValidation(param, value, props, input, patch)
            }

            return acc
         },
         {}
      )
   }

   hasChanges() {
      return 0 < _.values(this.state.patch).length
   }

   submitForm(ev) {
      ev.preventDefault()

      const formValidation = this.validateForm(),
         {onSubmit, input, transform} = this.props

      let {patch} = this.state

      if (0 < formValidation.length) {
         this.setState({
            state: {error: `There was ${formValidation.length} errors in the form`}
         })

         return
      }

      if (transform) {
         patch = transform(patch)
      }

      let promise = onSubmit(ev, patch, input)

      // should we care if another submit for this is in progress?
      if (!promise.then) {
         console.warn('form/onSubmit did not return a promise')
      } else {
         this.setState({updatePromise: promise, state: 'updating'})

         promise
            .then(() => this._mounted && this.setState({state: 'updated', patch: {}}))
            .catch(resp => this._mounted && this.setState({state: {error: resp}}))
      }
   }

   get submitState() {
      return this.state.state
   }

   render() {
      return (
         <form className="form" onSubmit={this.submitForm}>
            {this.props.children}
         </form>
      )
   }
}

Form.Updated = ({children}, {submitState}) => {
   if ('updated' === submitState) {
      return <Alert type="success" showIcon description={children} />
   } else {
      return null
   }
}

Form.Updated.propTypes = {
   children: React.PropTypes.node.isRequired
}

Form.Updated.contextTypes = {
   submitState: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object])
}

Form.Error = ({children}, {submitState}) => {
   if (_.isObject(submitState) && submitState.error) {
      return (
         <Alert
            type="error"
            showIcon
            message={children}
            description={submitState.error.toString()}
         />
      )
   } else {
      return null
   }
}

Form.Error.propTypes = {
   children: React.PropTypes.node.isRequired
}

Form.Error.contextTypes = {
   submitState: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object])
}

export const Reset = ({children}, ctx) => {
   return (
      <Button onClick={ctx.resetForm} disabled={'updating' === ctx.submitState}>

         {children}
      </Button>
   )
}

Reset.propTypes = {
   children: React.PropTypes.node.isRequired
}

Reset.contextTypes = {
   resetForm:   React.PropTypes.func,
   submitState: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object])
}

export const Submit = ({type, disabled, ...props}, {form, ...ctx}) => {
   const changed = form.hasChanges(),
      invalid = 0 < form.validateForm().length,
      submitting = 'updating' === ctx.submitState

   return (
      <Button
         onClick={form.submitForm}
         type={type || 'primary'}
         disabled={undefined !== disabled ? disabled : !changed || invalid || false}
         loading={submitting}
         {...props}
      >

         <Spinkit spin={'updating' === ctx.submitState} />

         {props.children}
      </Button>
   )
}

Submit.propTypes = {
   children: React.PropTypes.node.isRequired,
   type:     React.PropTypes.string,
   disabled: React.PropTypes.bool
}

Submit.contextTypes = {
   type:        React.PropTypes.string,
   form:        React.PropTypes.object,
   submitForm:  React.PropTypes.func,
   submitState: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object])
}

export class Select extends React.Component {
   static get propTypes() {
      return {
         feedback:      React.PropTypes.any,
         groupCssClass: React.PropTypes.array,
         label:         React.PropTypes.string,
         onUpdate:      React.PropTypes.func,
         value:         React.PropTypes.any,
         disabled:      React.PropTypes.bool,
         options:       React.PropTypes.array.isRequired,
         className:     React.PropTypes.string,
         param:         React.PropTypes.string.isRequired
      }
   }
   render() {
      const {onChange, input, patch, submitState} = this.context,
         updating = 'updating' === submitState,
         {
            disabled,
            feedback,
            className,
            groupCssClass,
            label,
            onUpdate,
            options,
            param,
            ...rest
         } = this.props

      let value = this.props.value

      if (undefined !== _.get(patch, param)) {
         value = _.get(patch, param)
      } else if (undefined !== _.get(input, param)) {
         value = _.get(input, param)
      }

      let inputCssClass = ('form-input form-select ' + className || '').split(/ /)

      return (
         <div className={_.uniq(groupCssClass).join(' ')}>
            {label && <label>{label}</label>}

            <AntdSelect
               className={_.uniq(inputCssClass).join(' ')}
               onChange={val => {
                  onChange(null, this.props, val)
                  if (onUpdate) {
                     onUpdate(ev)
                  }
               }}
               value={undefined !== value ? value.toString() : undefined}
               disabled={
                  updating ||
                     (_.isFunction(disabled) ? disabled(value, this.props, input, patch) : disabled)
               }
               {...rest}
            >

               {_.map(
                  options,
                  (opt, idx) =>
                     _.isPlainObject(opt)
                        ? <AntdSelect.Option key={idx} value={opt.value.toString()}>
                             {opt.label}
                          </AntdSelect.Option>
                        : <AntdSelect.Option key={idx} value={opt.toString()}>
                             {opt}
                          </AntdSelect.Option>
               )}

            </AntdSelect>

            {feedback}
         </div>
      )
   }
}

Select.contextTypes = {
   form:        React.PropTypes.object,
   onChange:    React.PropTypes.func,
   input:       React.PropTypes.object,
   patch:       React.PropTypes.object,
   submitState: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object])
}

// self validating input!
export class Input extends React.Component {
   componentWillMount() {
      // - register the input in the form
      //  - form may _.all(inputs, valid?)
      //  -> reply with errors?
      //  -> submit the thing
      this._node = null
      this.context.form.registerInput(this.props)
   }

   componentDidUpdate() {
      if (this._node) {
         this._node.focus()
      }
   }

   render() {
      const props = this.props,
         ctx = this.context

      let {onChange, input, patch} = ctx,
         inputValidated,
         {
            param,
            className,
            format,
            label,
            children,
            disabled,
            autoFocus,
            groupCssClass,
            value,
            feedback,
            onUpdate,
            ...rest
         } = props

      if (undefined !== _.get(patch, param)) {
         value = _.get(patch, param)
      } else if (undefined === value) {
         value = _.get(input, param)
      }

      inputValidated = doValidation(param, value, props, input, patch)

      let inputCssClass = (className || 'form-control').split(/ /)

      groupCssClass = (groupCssClass ? groupCssClass.split(/ /) : []).concat(
         children ? ['form-input', 'has-feedback'] : ['form-input']
      )

      if (_.isObject(inputValidated) && inputValidated.error) {
         groupCssClass = groupCssClass.concat(['has-feedback', 'has-error'])
      }

      if (!format) {
         format = _.identity
      }

      rest = _.omit(rest, 'transform', 'format', 'validate')

      let feedbackComponent

      if (feedback) {
         feedbackComponent = (
            <feedback
               value={format(undefined !== value ? value.toString() : '')}
               originalValue={_.get(input, param)}
               {...inputValidated}
            />
         )
      } else {
         feedbackComponent = !inputValidated.error
            ? null
            : <span className="help-block">{inputValidated.error}</span>
      }

      const updating = 'updating' === ctx.submitState

      let ref = null

      if (autoFocus) {
         ref = focusnode => {
            this._node = focusnode
         }
      }

      return (
         <div className={_.uniq(groupCssClass).join(' ')}>
            {label && <label htmlFor={'input-' + param}>{label}</label>}

            <AntdInput
               ref={ref}
               className={_.uniq(inputCssClass).join(' ')}
               id={'input-' + param}
               onChange={ev => {
                  onChange(ev, props)
                  if (onUpdate) {
                     onUpdate(ev)
                  }
               }}
               value={format(undefined !== value ? value.toString() : '')}
               disabled={
                  updating ||
                     (_.isFunction(disabled) ? disabled(value, props, input, patch) : disabled)
               }
               checked={value}
               {...rest}
            />

            {true === inputValidated && props.children}

            {feedback}
         </div>
      )
   }
}

Input.propTypes = {
   param:     React.PropTypes.string.isRequired,
   className: React.PropTypes.string,
   label:     React.PropTypes.string,
   transform: React.PropTypes.arrayOf(React.PropTypes.func),
   children:  React.PropTypes.node,
   feedback:  React.PropTypes.func
}

Input.contextTypes = {
   form:        React.PropTypes.object,
   onChange:    React.PropTypes.func,
   input:       React.PropTypes.object,
   patch:       React.PropTypes.object,
   submitState: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object])
}

class EditableInput extends React.Component {
   static get propTypes() {
      return {
         children: React.PropTypes.node.isRequired,
         value:    React.PropTypes.any.isRequired,
         param:    React.PropTypes.string.isRequired
      }
   }

   static get contextTypes() {
      return {
         form: React.PropTypes.object
      }
   }

   constructor() {
      super()

      this.state = {
         update:  undefined,
         editing: false
      }

      this.toggleEdit = this.toggleEdit.bind(this)
      this.updateValue = this.updateValue.bind(this)
   }

   toggleEdit(ev) {
      ev.preventDefault()

      this.setState(state => _.set(state, 'editing', !state.editing), this.focusInput)
   }

   updateValue(ev) {
      const val = ev.target.value

      this.setState(state => _.set(state, 'update', val))
   }

   render() {
      const {children, value, param} = this.props,
         {editing, update} = this.state,
         {patch} = this.context.form.state

      return (
         <div style={{display: 'inline'}}>
            <a style={{display: editing ? 'none' : 'inherit'}} onClick={this.toggleEdit}>

               {undefined !== update ? update || children : value || children}

               <Icon className="pull-right" type="edit" style={{paddingLeft: '1em'}} />
            </a>

            <Input
               onUpdate={this.updateValue}
               groupCssClass="input-group-sm"
               type="text"
               style={{display: !editing ? 'none' : 'inherit'}}
               param={param}
               onBlur={this.toggleEdit}
               feedback={() => (
                  <span
                     style={{
                        display: editing || undefined === _.get(patch, param, undefined)
                           ? 'none'
                           : 'inherit'
                     }}
                     className="input-meta"
                     title="Not saved"
                  >
                     <Icon type="exclamation-circle-o" />
                  </span>
               )}
               autoFocus={editing}
            />
         </div>
      )
   }
}

Input.Toggleable = EditableInput



// WEBPACK FOOTER //
// lib/forms/index.js